import React, { useState, useMemo } from 'react';
import { useCard } from '../../themes/cardStyles';
import { buildPaletteTheme } from '../../themes/palettes';
import { CampaignRune } from '../primitives/CampaignRune';
import { countdownLabel } from '../../derivations/schedule';

export const CampaignCard = ({
  theme,
  density,
  camp,
  palette,
  cardStyle,
  cardFinish = 'solid',
  dimmed = false,
  onHoverChange,
  onClick,
  onEnter,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [hovered, setHovered] = useState(false);

  // Apply the campaign palette as a *theme override* for this card's style.
  // Two finishes:
  //   'solid'   — flat opaque surface, tight 1px solid palette border, no inset
  //               highlight or outer halo. Reads as a defined card on the page.
  //   'tinted'  — top-to-bottom gradient, soft inset highlight, soft 1px halo
  //               in palette color. Reads as a translucent panel.
  const tintedTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish),
    [theme, palette, cardStyle, cardFinish]
  );

  const cardStyleObj = useCard(tintedTheme, cardStyle, hovered);

  // For solid finish, layer a palette-colored border on the card style.
  // Bordered cards get a visibly stronger outline because the border is
  // the style's whole identity; other styles get a whisper-thin 1px line
  // for a crisp defined edge without competing with the fill or shadow.
  const solidBorderStyle = cardFinish === 'solid' ? (
    cardStyle === 'bordered'
      ? { border: `1.5px solid ${palette.color}` }
      : { border: `1px solid ${palette.color}55` }
  ) : {};

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => { setHovered(true); onHoverChange && onHoverChange(true); }}
      onMouseLeave={() => { setHovered(false); onHoverChange && onHoverChange(false); }}
      style={{
        ...cardStyleObj,
        ...solidBorderStyle,
        padding: density.spacing.cardPadding,
        marginBottom: density.spacing.itemGap,
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        // When another card in the list is hovered, this one recedes:
        // opacity drops + slight desaturation pulls it visually into the
        // background so the hovered card pops forward.
        opacity: dimmed ? 0.65 : 1,
        filter: dimmed ? 'saturate(0.8)' : 'none',
        transition: `${e.transition || 'all 200ms ease'}, opacity 180ms ease, filter 180ms ease`,
      }}
    >
      {/* Tonight gradient — rises from the bottom of the card in palette
          color. The "inverse" of the regular hover lift (which brightens
          from the top). Signals this campaign is the active session
          without competing with the card's existing chrome. */}
      {countdownLabel(camp.schedule) === 'Tonight' && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(0deg, ${palette.color}33 0%, ${palette.color}11 35%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }} />
      )}

      {/* Left-edge palette stripe (for 'flat' style especially) */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: 3,
        background: palette.color,
        opacity: hovered ? 1 : 0.7,
        zIndex: 1,
      }} />

      <CampaignRune
        name={camp.rune}
        size={52}
        color={hovered ? palette.light + 'cc' : palette.light + '55'}
        glow={hovered ? 1 : 0}
        style={{
          flexShrink: 0,
        }}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: t.headingFamily || 'Georgia, serif',
          fontSize: '1.2rem',
          fontWeight: 600,
          color: palette.light,
          textShadow: `${e.headingTextShadow || ''}, 0 0 22px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.6)`,
          marginBottom: 3,
        }}>{camp.name}</div>
        <div style={{ fontSize: '0.82rem', color: c.textSecondary, marginBottom: 8, fontFamily: t.fontFamily }}>
          {camp.dm && (
            <>DM <span style={{ color: c.textPrimary, fontWeight: 600 }}>{camp.dm}</span></>
          )}
          {(camp.players > 0 || camp.schedule) && (
            <span style={{ color: c.textMuted }}>
              {camp.dm ? ' · ' : ''}
              {camp.players > 0 ? `${camp.players} player${camp.players === 1 ? '' : 's'}` : ''}
              {camp.players > 0 && camp.schedule ? ' · ' : ''}
              {camp.schedule || ''}
            </span>
          )}
          {camp.role && !camp.dm && (
            <span style={{ color: c.textMuted, textTransform: 'capitalize' }}>{camp.role}</span>
          )}
        </div>
        {camp.blurb && (
          <div style={{
            fontSize: '0.84rem',
            fontStyle: 'italic',
            color: c.textSecondary,
            opacity: 0.85,
            marginBottom: 12,
            lineHeight: 1.5,
            fontFamily: t.fontFamily,
          }}>"{camp.blurb}"</div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '0.65rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: c.textMuted,
            fontWeight: 700,
            fontFamily: t.fontFamily,
          }}>Party</span>
          {(camp.party && camp.party.length > 0) ? camp.party.map((p, i) => {
            const isYou = p === camp.yourChar;
            if (isYou) {
              // Your character gets a palette-colored pill
              return (
                <span key={i} style={{
                  fontSize: '0.78rem',
                  color: palette.light,
                  padding: '2px 11px',
                  background: palette.bg,
                  border: `1px solid ${palette.color}`,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontFamily: t.fontFamily,
                  boxShadow: `0 0 8px ${palette.glow}`,
                }}>{p}</span>
              );
            }
            return (
              <span key={i} style={{
                fontSize: '0.78rem',
                color: c.textMuted,
                fontStyle: 'italic',
                fontFamily: t.fontFamily,
              }}>
                {p}{i < camp.party.length - 1 ? ' ·' : ''}
              </span>
            );
          }) : (
            <span style={{
              fontSize: '0.78rem',
              color: c.textMuted,
              fontStyle: 'italic',
              fontFamily: t.fontFamily,
              opacity: 0.7,
            }}>No players added yet</span>
          )}
        </div>
      </div>

      <div style={{
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}>
        <button
          onClick={(ev) => { ev.stopPropagation(); (onEnter || onClick)?.(); }}
          style={{
            padding: '8px 16px',
            background: hovered
              ? `linear-gradient(180deg, ${palette.color}, ${palette.color}cc)`
              : palette.bg,
            border: `1px solid ${palette.color}`,
            borderRadius: e.borderRadiusSm || '6px',
            color: hovered ? c.textOnAccent : palette.light,
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: t.fontFamily,
            whiteSpace: 'nowrap',
            transition: e.transition,
            boxShadow: hovered ? `0 4px 14px ${palette.glow}` : 'none',
          }}
        >Enter →</button>
      </div>
    </div>
  );
};
